import { ChargingLocation, ChargePoint, Filters, Vehicle } from '../types';
import { locationMatchesPowerCategories } from './powerCategories';

/**
 * FilterPanel historically stored display labels ("CCS DC") while locations
 * and vehicles store backend connector IDs ("ccs2"). Map both to one id.
 */
const CONNECTOR_ALIASES: Record<string, string> = {
  type2: 'type2',
  'type 2 ac': 'type2',
  'type 2 (iec 62196)': 'type2',
  ccs2: 'ccs2',
  'ccs dc': 'ccs2',
  'ccs type 2': 'ccs2',
  'ccs combo 2 (iec 62196)': 'ccs2',
  chademo: 'chademo',
  'chademo (dc)': 'chademo',
};

function canonicalizeConnector(type: string): string {
  const key = type.trim().toLowerCase();
  return CONNECTOR_ALIASES[key] ?? key;
}

function chargePointConnectorTypes(cp: ChargePoint): string[] {
  const types = new Set<string>();
  if (cp.type) types.add(canonicalizeConnector(cp.type));
  for (const evse of cp.evses ?? []) {
    if (evse.connectorType) types.add(canonicalizeConnector(evse.connectorType));
  }
  return [...types];
}

function locationHasConnector(location: ChargingLocation, wanted: string[]): boolean {
  const wantedSet = new Set(wanted.map(canonicalizeConnector));
  return location.chargepoints.some((cp) =>
    chargePointConnectorTypes(cp).some((type) => wantedSet.has(type))
  );
}

/**
 * Applies filters to a list of charging locations
 */
export function applyFilters(
  locations: ChargingLocation[],
  filters: Filters,
  vehicles?: Vehicle[]
): ChargingLocation[] {
  let filtered = locations;

  // Filter by selected vehicle's connector types
  if (filters.selectedVehicleId && vehicles) {
    const selectedVehicle = vehicles.find(v => v.id === filters.selectedVehicleId);
    if (selectedVehicle && selectedVehicle.connectorTypes.length > 0) {
      filtered = filtered.filter((location) =>
        locationHasConnector(location, selectedVehicle.connectorTypes)
      );
    }
  }

  // Filter by manually selected connector types
  if (filters.connectorTypes.length > 0) {
    filtered = filtered.filter((location) =>
      locationHasConnector(location, filters.connectorTypes)
    );
  }

  if (filters.facilities.length > 0) {
    filtered = filtered.filter((location) => {
      const have = new Set(
        (location.facilities ?? []).map((f) => f.trim().replace(/\s+/g, '_').toUpperCase())
      );
      return filters.facilities.some((facility) =>
        have.has(facility.trim().replace(/\s+/g, '_').toUpperCase())
      );
    });
  }

  // Filter by power categories
  if (filters.powerCategories.length > 0) {
    filtered = filtered.filter((location) =>
      locationMatchesPowerCategories(location.chargepoints, filters.powerCategories)
    );
  }

  return filtered;
}

/**
 * Checks if any filters are currently active
 */
export function hasActiveFilters(filters: Filters): boolean {
  return filters.connectorTypes.length > 0 ||
         filters.facilities.length > 0 ||
         filters.powerCategories.length > 0 ||
         !!filters.selectedVehicleId;
}

/**
 * Gets count of available stations and chargepoints
 */
export function getAvailabilityStats(locations: ChargingLocation[]) {
  const availableStations = locations.filter((l) => l.available).length;
  const totalChargepoints = locations.reduce(
    (acc, l) => acc + l.chargepoints.filter((cp) => cp.status === 'available').length,
    0
  );

  return {
    availableStations,
    totalChargepoints,
  };
}
